import React, { useContext, useEffect, useState } from 'react';
import { Button } from 'primereact/button';
import {
  CompanyLocationRampWithOpeningHoursOutDTO,
  EntityTypeEnum,
  ShipmentPageR2SLocationOutDTO,
} from '@api/ringil-api.v2';
import { Identifier, isNullOrUndefined } from '@utils/utils';
import {
  dayjsAtStartOfDayInTz,
  dayjsFromApiUtcInTz,
  dayjsNowInTz,
  formatDateFromDayjsWithLocale,
  formatTimeIntervalFromDayjsIntervalWithLocale,
  stringYMDFromDayjs,
  timeSlotProgressTabDtoComparator,
} from '@utils/dateWithTz.tsx';
import { ApiContext } from '@api/api';
import styled from 'styled-components';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { resolveLoadingUnloadingStateText } from '@utils/handlers';
import LinkedResBadge from '@components/ringil3/Badges/LinkedResBadge';
import CanMakeReservation from '@components/ringil3/Features/Reservation/Helper/CanMakeReservation';
import { useTranslation } from 'react-i18next';
import { ExpeditionDetailHandlerContext } from '@app/pages/carrier/expedition-detail/ExpeditionDetailHandler.tsx';
import {
  useCreateChangeVehicleInfo,
} from '@app/pages/carrier/expedition-detail/ChangeVehicleInfo/ChangeVehicleInfo.tsx';
import { useCreateTimeslotSidebar } from '@components/ringil3/Features/Reservation/Sidedbars/CreateTimeslotSidebar.tsx';
import HorizontalFieldValue from '@components/obsolete/HorizontalFieldValue.tsx';
import InfoNote from '@components/obsolete/InfoNote/InfoNote.tsx';
import CollapsibleNote from '@components/RingilComponents/RichTextEditor/CollapsibleNote.tsx';
import NavTitle from '@components/obsolete/NavTitle/NavTitle.tsx';
import PanelWithIcon from '@components/obsolete/PanelWithIcon/PanelWithIcon.tsx';
import { ButtonRowNoWrap } from '@components/styles.tsx';
import LoadingUnloadingConfirmDialog from '@app/pages/carrier/common/LoadingUnloadingConfirmDialog.tsx';
import Label from '@components/obsolete/Form/Label/Label.tsx';
import TimeList from '@components/obsolete/TimeList/TimeList.tsx';
import TimeListItem from '@components/obsolete/TimeList/TimeListItem.tsx';
import { AdditionalCosts } from '@components/obsolete/AdditionalCosts/AdditionalCosts.tsx';
import CarrierChatComponent from '@app/pages/carrier/Chat/CarrierChatComponent.tsx';
import EditCarrierTimeslot from '@app/pages/carrier/expedition-detail/EditCarrierTimeslot/EditCarrierTimeslot.tsx';
import HereMaps from '@components/features/HereMap/HereMaps.tsx';
import { formatPrice } from '@utils/priceUtils.ts';
import { toCompactDateTimeRangePart } from '@utils/carrierShipmentTestIds';
import { getLocationPositionKey } from '@utils/testUtils';

interface ComponentProps {
  toogleToExpeditionPlan: () => void;
  disabled?: boolean;
}

const TabShipmentActive2Finish: React.FC<ComponentProps> = ({ toogleToExpeditionPlan, disabled = false }) => {
  const { t }: { t: any } = useTranslation('common');
  const {
    state,
    confirmAction,
    loadTabActivePlus,
    changeCarrierSlots,
  } = useContext(ExpeditionDetailHandlerContext);
  const [visibleChatDialog, setVisibleChatDialog] = useState(false);
  const [visibleSlotEditSidebar, setVisibleSlotEditSidebar] = useState(false);
  const {getSharedRampsForCompanyLocation } = useContext(ApiContext);
  const [rampsByLocationId, setRampsByLocationId] = useState<Record<string, CompanyLocationRampWithOpeningHoursOutDTO[]>>({});

  useEffect(() => {
    loadTabActivePlus();
  }, []);

  useEffect(() => {
    if (state.activeStateData && state.activeStateData.locations) {
      state.activeStateData.locations.forEach(location => {
        console.log('TabShipmentActive2Finish: Processing location:', JSON.stringify(location, null, 2));
        const companyLocationIdForRampFetching = location.companyLocationId;
        const customerIdAtLocation = location.companyLocationCustomerId;

        if (companyLocationIdForRampFetching && customerIdAtLocation && rampsByLocationId[companyLocationIdForRampFetching] === undefined) {
          setRampsByLocationId(prevRamps => ({ ...prevRamps, [companyLocationIdForRampFetching]: null }));
          getSharedRampsForCompanyLocation(companyLocationIdForRampFetching, customerIdAtLocation, true, ramps => {
            setRampsByLocationId(prevRamps => ({ ...prevRamps, [companyLocationIdForRampFetching]: ramps || [] }));
          });
        }
      });
    }
  }, [state.activeStateData, getSharedRampsForCompanyLocation]);

  const resolveStateColor = (location: ShipmentPageR2SLocationOutDTO): string => {
    if (location.isComplete && location.inTime) return 'green';
    if (location.isComplete && !location.inTime) return 'red';
    if (!location.isComplete && location.inTime) return 'orange';
    if (!location.isComplete && !location.inTime) return 'orange';
  };

  const [visibleMarkAsFinishedDialog, setVisibleMarkAsFinishedDialog] = useState<{
    [key: string]: { visible: boolean; locationAction: string };
  }>({});

  const setVisibilityMarkAsFinishedDialog = (locationId: Identifier, metaData: {
    visible: boolean;
    locationAction: string
  }) => {
    setVisibleMarkAsFinishedDialog({
      ...visibleMarkAsFinishedDialog,
      [locationId]: metaData,
    });
  };

  const isVisibleMarkAsFinishedDialog = (locationId: Identifier): { visible: boolean; locationAction: string } => {
    if (isNullOrUndefined(visibleMarkAsFinishedDialog[locationId])) return null;
    return visibleMarkAsFinishedDialog[locationId];
  };

  const { tr } = useTranslationLgs();

  const { ChangeVehicleInfo, setVisible: setVisibleChangeVehicleInfo } = useCreateChangeVehicleInfo();
  const { CreateTimeslotSidebar, setCreateTimeslotRequest, createTimeslotRequest } = useCreateTimeslotSidebar();

  const computePreferredDate = (
    location: ShipmentPageR2SLocationOutDTO,
  ): string => {

    if (location.selectedTimeslots.length > 0) {
      const dayjsAtStartOfDay = location.timezoneId != null
        ? dayjsAtStartOfDayInTz(dayjsNowInTz(location.timezoneId), location.timezoneId)
        : dayjsAtStartOfDayInTz(dayjsNowInTz('Europe/Prague'), 'Europe/Prague');

      const firstFutureSlot = location.selectedTimeslots
        .filter(slot => {
          const slotDayjs = location.timezoneId != null
            ? dayjsFromApiUtcInTz(slot.utcStart, location.timezoneId)
            : dayjsFromApiUtcInTz(slot.sinceInUTC, 'Europe/Prague');
          return slotDayjs.isAfter(dayjsAtStartOfDay);
        })
        .filter(slot => slot.isSelected)
        .sort((a, b) => timeSlotProgressTabDtoComparator(a,b, location.timezoneId))[0];

      if (firstFutureSlot) {
        const date = location.timezoneId != null
          ? dayjsFromApiUtcInTz(firstFutureSlot.utcStart, firstFutureSlot.timezoneId)
          : dayjsFromApiUtcInTz(firstFutureSlot.sinceInUTC, 'Europe/Prague');
        return stringYMDFromDayjs(date);
      }
    }

    // today date is good enough
    return new Date().toISOString().split('T')[0];
  };

  const getLocationPrefix = (location: ShipmentPageR2SLocationOutDTO, locationIndex: number) =>
    `shipmentProgress-${getLocationPositionKey(location.locationId, locationIndex, state.activeStateData.locations.length)}`;

  return (
    <>
      {state.activeStateData && (
        <>
          <div className="p-d-flex p-grid">
            <div className="p-col-12 p-md-6 p-lg-6 p-mr-6">
              {state.activeStateData.carrierNote && (
                <HorizontalFieldValue label={tr(`TabShipmentActive2Finish.carrierNote`, `Carrier note`)}
                                      value={<InfoNote text={<CollapsibleNote html={state.activeStateData.carrierNote} bgClassName={'bg-[color:var(--info-alert-background-color)]'} />} />}
                                      dataTestId="shipmentProgress-noteForCarrier" />
              )}

              <NavTitle>
                <h3 className="p-mt-0" style={{ fontWeight: 'normal' }}>
                  {t(`TabShipmentActive2Finish.expedition`, `Expedice`)}
                </h3>
                {state.header?.disableCarrierProgressTimeslots !== true && (
                  <Button
                    label={t(`TabShipmentActive2Finish.change`, `Change`)}
                    iconPos="left"
                    icon="icon pi pi-pencil"
                    className="p-button-outlined p-btn-edit p-icon-hide "
                    onClick={() => setVisibleSlotEditSidebar(true)}
                    disabled={disabled}
                  />
                )}
              </NavTitle>

              {state.activeStateData.locations.map((location, locationIndex) => {
                const locationPrefix = getLocationPrefix(location, locationIndex);
                return (
                  <div className="p-grid expedition-time-line timeslots-wrapper" key={locationIndex}>
                    <div className="p-col-12 p-xl-6 p-flex p-flex-row">
                      <PanelWithIcon nameIcon="pi pi-clock" colorIcon={resolveStateColor(location)}>
                        <Col>
                          <p>
                            <strong>{`${location.zipCode}, ${location.city} (${location.country})`}</strong>
                          </p>
                          <p>
                            <small className="text-gray5" data-testid={`${locationPrefix}-locationName`}>{location.name}</small>
                          </p>
                          <p>
                            <small data-testId={`${locationPrefix}-locationFinished`} className={`text-${resolveStateColor(location)}`}>
                              {resolveLoadingUnloadingStateText(
                                location.isComplete,
                                location.completedAt,
                                location.loadingActionIsFinished,
                                location.loadingActionFinishedAt,
                                location.loadingActionFinishedAtUtc,
                                location.unloadingActionIsFinished,
                                location.unloadingActionFinishedAt,
                                location.unloadingActionFinishedAtUtc,
                                location.timezoneId,
                                t,
                              )}
                            </small>
                          </p>
                        </Col>
                        <ButtonRowNoWrap>
                          {!location.isComplete && isNullOrUndefined(location.unloadingActionIsFinished) && isNullOrUndefined(location.loadingActionIsFinished) && (
                            <Button
                              label={tr(`TabShipmentActive2Finish.finishLocation`, `Finish location`)}
                              onClick={e =>
                                setVisibilityMarkAsFinishedDialog(location.locationId, {
                                  visible: true,
                                  locationAction: 'noActionFinishedAt',
                                })
                              }
                              data-testid={`${locationPrefix}-finishLocation`}
                            />
                          )}
                          {location.loadingActionIsFinished === false && (
                            <Button
                              label={tr(`TabShipmentActive2Finish.finishLoading`, `Finish loading`)}
                              onClick={e =>
                                setVisibilityMarkAsFinishedDialog(location.locationId, {
                                  visible: true,
                                  locationAction: 'loadingLocationFinished',
                                })
                              }
                              data-testid={`${locationPrefix}-finishLoading`}
                            />
                          )}
                          {location.unloadingActionIsFinished === false && (
                            <Button
                              label={tr(`TabShipmentActive2Finish.finishUnloading`, `Finish unloading`)}
                              onClick={e =>
                                setVisibilityMarkAsFinishedDialog(location.locationId, {
                                  visible: true,
                                  locationAction: 'unloadingLocationFinished',
                                })
                              }
                              data-testid={`${locationPrefix}-finishUnloading`}
                            />
                          )}
                        </ButtonRowNoWrap>

                        <LoadingUnloadingConfirmDialog
                          locationId={location.locationId}
                          zipCode={location.zipCode}
                          countryCode={location.country}
                          timezoneId={location.timezoneId}
                          onConfirm={(locationId, when, timezoneId) => {
                            confirmAction(locationId, when, timezoneId, isVisibleMarkAsFinishedDialog(location.locationId)?.locationAction);
                            setVisibilityMarkAsFinishedDialog(location.locationId, null);
                          }}
                          onCancel={() => setVisibilityMarkAsFinishedDialog(location.locationId, null)}
                          visible={isVisibleMarkAsFinishedDialog(location.locationId)?.visible}
                          propertyName={isVisibleMarkAsFinishedDialog(location.locationId)?.locationAction}
                          dataTestId="confirmLoadingUnloading"
                        />
                      </PanelWithIcon>
                    </div>
                    <div className="p-col-12 p-xl-6">
                      <PanelWithIcon srcIcon="/images/calendar.svg">
                        <Label title={t(`TabShipmentActive2Finish.carrierSTimeslot`, `Časové okno dopravce`)} />
                        <TimeList>
                          <>
                            {location.selectedTimeslots
                              .filter(s => s.isSelected)
                              .map((timeslot, timeslotIndex) => {
                                const slotSince = timeslot.timezoneId != null ? timeslot.utcStart : timeslot.sinceInUTC;
                                const slotTill = timeslot.timezoneId != null ? timeslot.utcEnd : timeslot.tillInUTC;
                                const timezoneId =  timeslot.timezoneId ?? 'Europe/Prague';
                                const mappedSince = dayjsFromApiUtcInTz(slotSince, timezoneId)
                                return (
                                  <TimeListItem
                                    key={timeslotIndex}
                                    date={formatDateFromDayjsWithLocale(dayjsFromApiUtcInTz(slotSince, timezoneId))}
                                    isWholeDay={timeslot.isNotSpecifiedTime}
                                    time={`${formatTimeIntervalFromDayjsIntervalWithLocale(dayjsFromApiUtcInTz(slotSince, timezoneId), dayjsFromApiUtcInTz(slotTill, timezoneId), timezoneId)}`}
                                    timezoneId={timezoneId}
                                    dataTestId={`${locationPrefix}`}
                                    data-testid={`${locationPrefix}-expectedHandling-${toCompactDateTimeRangePart(
                                      mappedSince.format('YYYY-MM-DD'),
                                      mappedSince.format('HH:mm'),
                                      timeslot.isNotSpecifiedTime ? '23:59' : dayjsFromApiUtcInTz(slotTill, timezoneId).format('HH:mm'),
                                    )}`}
                                  />
                                );
                              })}
                          </>
                          <div className={'p-mt-2'}>
                            {location.linkedReservation &&
                              <LinkedResBadge linkedReservation={location.linkedReservation} />}
                            {isNullOrUndefined(location.linkedReservation) &&
                              Array.isArray(rampsByLocationId[location.companyLocationId]) &&
                              rampsByLocationId[location.companyLocationId].length > 0 && (
                                <CanMakeReservation companyLocationId={location.companyLocationId}>
                                  <Button
                                    label={tr(`TabShipmentActive2Finish.createReservation`, `Create reservation`)}
                                    onClick={e => {
                                      setCreateTimeslotRequest({
                                        applicationId: state.header.applicationId,
                                        companyLocationId: location.companyLocationId,
                                        preferredDate: computePreferredDate(location),
                                        companyLocationCustomerId: location.companyLocationCustomerId,
                                        expeditionLocationId: location.locationId,
                                        expeditionId: state.header.id,
                                        timezoneId: rampsByLocationId[location.companyLocationId]?.[0]?.timezoneId,
                                        testIdPrefix: locationPrefix,
                                      });
                                    }}
                                    data-testid={`${locationPrefix}-expectedHandling-newReservation`}
                                  />
                                </CanMakeReservation>
                              )}
                          </div>
                        </TimeList>
                      </PanelWithIcon>
                    </div>
                  </div>
                );
              })}

              <AdditionalCosts applicationId={state.header.applicationId} companyId={state.header.companyId}
                               editable={false} />

              {createTimeslotRequest && (
                <CreateTimeslotSidebar
                  onContinue={() => {
                    loadTabActivePlus();
                  }}
                />
              )}

              <Button
                label={t(`TabShipmentActive2Finish.expeditionRoute`, `Plán expedice`)}
                iconPos="right"
                icon="pi pi-chevron-right"
                className="p-pl-0 p-pr-0 p-mt-2 p-mb-6 p-button-text"
                onClick={toogleToExpeditionPlan}
              />
              <NavTitle>
                <h3 className="p-mt-0" style={{ fontWeight: 'normal' }}>
                  {t(`TabShipmentActive2Finish.assignedCar`, `Assigned car`)}
                </h3>
                <Button
                  label={t(`TabShipmentActive2Finish.edit`, `Upravit`)}
                  iconPos="left"
                  icon="icon pi pi-pencil"
                  className="p-button-outlined p-btn-edit p-icon-hide "
                  onClick={() => setVisibleChangeVehicleInfo(true)}
                  disabled={disabled}
                />
              </NavTitle>
              <div className="timeslots-wrapper" style={{ position: 'relative' }}>
                <PanelWithIcon srcIcon="/images/contacts.svg">
                  <p>
                    <strong className="p-mr-3">{t(`TabShipmentActive2Finish.plates`, `SPZ`)}</strong>
                    <span data-testid="shipmentProgress-carPlate">{state.activeStateData.carInfo.carPlate}</span>
                  </p>
                  <p>
                    <strong className="p-mr-3">{t(`TabShipmentActive2Finish.driver`, `Řidič`)}</strong>
                    <span data-testid="shipmentProgress-driver">{state.activeStateData.carInfo.driverName}</span>
                  </p>
                  <p>
                    <strong className="p-mr-3">{t(`TabShipmentActive2Finish.dispatcher`, `Dispečer`)}</strong>
                    <span data-testid="shipmentProgress-dispatcher">{state.activeStateData.carInfo.dispatcherName}</span>
                  </p>
                </PanelWithIcon>
              </div>
              <h3 className="p-mt-0" style={{ fontWeight: 'normal' }}>
                {t(`TabShipmentActive2Finish.customer`, `Zákazník`)}
              </h3>
              <PanelWithIcon srcIcon="/images/contacts.svg">
                <Label title={state.activeStateData.customerInfo.name} />
                <div data-testid="shipmentProgress-customerName" style={{ display: 'none' }}>{state.activeStateData.customerInfo.name}</div>
                <p>{`${state.activeStateData.customerInfo.streetNr}, ${state.activeStateData.customerInfo.city}, (${state.activeStateData.customerInfo.country})`}</p>
              </PanelWithIcon>
              <PanelWithIcon srcIcon="/images/user.svg">
                <p data-testid="shipmentProgress-responsiblePerson">{state.activeStateData.customerInfo.responsiblePersonFullName}</p>
                <p className="text-gray5">
                  <small>{t(`TabShipmentActive2Finish.responsiblePerson`, `Zodpovědná osoba`)}</small>
                </p>
              </PanelWithIcon>
              <PanelWithIcon srcIcon="/images/envelope.svg">
                <Button
                  icon="pi pi-envelope"
                  className="p-button-outlined "
                  label={t(`TabShipmentActive2Finish.writeMessage`, `Napsat zprávu`)}
                  onClick={() => setVisibleChatDialog(true)}
                  data-testid="shipmentProgress-writeMessage"
                />
              </PanelWithIcon>
              <PanelWithIcon srcIcon="/images/money.svg">
                <p>{t(`TabShipmentActive2Finish.agreedPrice`, `Smluvená cena`)}</p>
                <p>
                  <strong data-testid="shipmentProgress-price">{formatPrice(state.activeStateData.customerInfo.price, state.activeStateData.customerInfo.currency)}</strong>
                </p>
              </PanelWithIcon>
            </div>
            <div className="p-col-12 p-md-5">
              <Label title={tr(`TabShipmentActive2Finish.map`, `Map`)} />
              <HereMaps customerId={state.header.companyId} applicationId={state.header.applicationId} />
            </div>
          </div>
          <CarrierChatComponent
            companyId={state.header.companyId}
            entityId={state.header.id}
            entityType={EntityTypeEnum.EXPEDITION}
            visible={visibleChatDialog}
            onHide={() => {
              setVisibleChatDialog(false);
            }}
            customerName={state.header.customerName}
          />
          <EditCarrierTimeslot
            visible={visibleSlotEditSidebar}
            onHide={() => setVisibleSlotEditSidebar(false)}
            companyId={state.header.companyId}
            applicationId={state.header.applicationId}
            disableCarrierProgressTimeslots={state.header?.disableCarrierProgressTimeslots === true}
            onUpdateSlot={data => {
              changeCarrierSlots(data);
              setVisibleSlotEditSidebar(false);
            }}
          />
          <ChangeVehicleInfo
            onComplete={() => {
              // reload
              loadTabActivePlus();
            }}
            data={{
              driverId: state.activeStateData.carInfo.driverId,
              driverName: state.activeStateData.carInfo.driverName,
              carPlate: state.activeStateData.carInfo.carPlate,
              dispatcherId: state.activeStateData.carInfo.dispatcherId,
            }}
            expeditionId={state.header.id}
            showDispatcher={true}
            showDriver={true}
          />
        </>
      )}
    </>
  );
};
const Col = styled.div`
  display: flex;
  flex-direction: column;
`;

export default TabShipmentActive2Finish;
